import { mergeAttributes, Node } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { Suggestion, type SuggestionOptions } from "@tiptap/suggestion";

import {
  formatMentionToken,
  mentionKey,
} from "@hightouch/lib/llm/asset-comments/mention-tokens";
import type { CommentMention } from "@hightouch/lib/llm/asset-comments/types";

import type { MentionOption } from "./mention-options-list";

export const MENTION_NODE_NAME = "assetCommentMention";
export const MENTION_CHAR = "@";

/**
 * Reads a node's attributes back into a mention target.
 *
 * Attributes are `any` as far as ProseMirror is concerned — they survive
 * copy-paste and HTML round-trips — so the kind and id are checked rather than
 * asserted.
 */
function toMentionAttrs(attrs: Record<string, unknown>): CommentMention | null {
  const { kind, id } = attrs;

  if (kind === "user") {
    const userId = Number(id);
    return Number.isSafeInteger(userId) && userId > 0
      ? { kind: "user", userId }
      : null;
  }

  if (kind === "reviewAgent" && typeof id === "string" && id.length > 0) {
    return { kind: "reviewAgent", reviewAgentId: id };
  }

  if (kind === "agent") {
    return { kind: "agent" };
  }

  return null;
}

/**
 * A mention node's attributes as the option the mutation takes.
 *
 * The composer builds its mention list from the same nodes the body's tokens
 * are written from, and validity comes from `toMentionAttrs` either way — so a
 * node contributes to both the body and the list, or to neither. Reading the
 * list from what the user picked in this session instead would drop any mention
 * that got into the document another way (pasted, or restored with a draft)
 * while `renderText` still wrote its token, and the service rejects that pair.
 */
export function toMentionOption(
  attrs: Record<string, unknown>,
): MentionOption | null {
  const mention = toMentionAttrs(attrs);

  if (!mention) {
    return null;
  }

  // Matches the token's label, which falls back the same way.
  const label =
    typeof attrs.label === "string" ? attrs.label : String(attrs.id);

  switch (mention.kind) {
    case "user":
      return {
        kind: "user",
        userId: mention.userId,
        value: String(mention.userId),
        label,
      };
    case "reviewAgent":
      return {
        kind: "reviewAgent",
        reviewAgentId: mention.reviewAgentId,
        value: mention.reviewAgentId,
        label,
      };
    case "agent":
      return { kind: "agent", value: mentionKey(mention), label };
  }
}

export const MentionPluginKey = new PluginKey(MENTION_NODE_NAME);

type MentionNodeOptions = {
  suggestion: Omit<SuggestionOptions, "editor">;
};

/**
 * The `@`-mention node for asset comments.
 *
 * Deliberately not the shared RTE typeahead plugin: that one renders through a
 * React `NodeViewWrapper`, which is a block-level `div` and so forces every
 * mention onto its own line. That's correct for the Slack composer it was built
 * for and wrong here, where a mention sits mid-sentence. Changing it in place
 * would alter that surface, so this is a separate node.
 *
 * Rendered as an inline atom: it can't be half-deleted and sits in the flow of
 * the sentence. It serializes to `@[Name](user:4821)` — the id is what
 * identifies the target, and the name rides along only so the raw body stays
 * readable. See `mention-tokens` for the format.
 */
export const MentionNode = Node.create<MentionNodeOptions>({
  name: MENTION_NODE_NAME,
  group: "inline",
  inline: true,
  atom: true,
  selectable: false,

  addOptions() {
    return {
      suggestion: {
        char: MENTION_CHAR,
        pluginKey: MentionPluginKey,
        command: ({ editor, range, props }) => {
          editor
            .chain()
            .focus()
            // Trailing space so typing continues after the mention rather than
            // inside it.
            .insertContentAt(range, [
              { type: MENTION_NODE_NAME, attrs: props },
              { type: "text", text: " " },
            ])
            .run();
        },
      },
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-mention-id"),
        renderHTML: (attributes) =>
          attributes.id ? { "data-mention-id": attributes.id } : {},
      },
      // Which of the two target kinds the id names. Carried on the node because
      // the serialized token namespaces it — a user and an agent can hold the
      // same id text and must not collapse into one target.
      kind: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-mention-kind"),
        renderHTML: (attributes) =>
          attributes.kind ? { "data-mention-kind": attributes.kind } : {},
      },
      label: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-mention-label"),
        renderHTML: (attributes) =>
          attributes.label ? { "data-mention-label": attributes.label } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[data-type="${MENTION_NODE_NAME}"]` }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes({ "data-type": MENTION_NODE_NAME }, HTMLAttributes),
      `${MENTION_CHAR}${node.attrs.label ?? node.attrs.id}`,
    ];
  },

  /**
   * The stored comment body. Emits the full token so the text identifies who it
   * mentions; the editor still *displays* `@Name` via `renderHTML` above.
   */
  renderText({ node }) {
    const mention = toMentionAttrs(node.attrs);

    // A node without usable attrs can't name a target. Fall back to the plain
    // label so the sentence still reads, and let the body carry no mention
    // rather than a token pointing nowhere.
    if (!mention) {
      return `${MENTION_CHAR}${node.attrs.label ?? node.attrs.id ?? ""}`;
    }

    return formatMentionToken(
      mention,
      node.attrs.label ?? String(node.attrs.id),
    );
  },

  addProseMirrorPlugins() {
    return [Suggestion({ editor: this.editor, ...this.options.suggestion })];
  },
});
